import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowBack, Check, CheckCircle, ExpandLess, ExpandMore, MoreHoriz, Search, ShoppingCartCheckout, WhatsApp } from '@mui/icons-material';
import ExperimentalPopupProvider from '../ExperimentalPopups/ExperimentalPopupProvider';
import { previewIngredients, previewLists } from './listasPreviewData';
import './ShoppingListsV2.css';

export default function ShoppingListDetailV2({ preview = false }) {
  const { id } = useParams();
  const [list, setList] = useState(preview ? previewLists[0] : null);
  const [ingredients, setIngredients] = useState(preview ? previewIngredients : []);
  const [loading, setLoading] = useState(!preview);
  const [showBought, setShowBought] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (preview) return;
    const load = async () => {
      try {
        const [listResponse, ingredientResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/listascompra/${id}`),
          fetch(`${import.meta.env.VITE_API_URL}/ingredients`),
        ]);
        if (!listResponse.ok) throw new Error('No se pudo cargar la lista');
        setList(await listResponse.json());
        setIngredients(await ingredientResponse.json());
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    load();
  }, [id, preview]);

  const handleToggle = async (originalIndex, ingredientId) => {
    const previous = list.ingredientes[originalIndex].estado;
    const estado = previous === 'comprado' ? 'pendiente' : 'comprado';
    setList((current) => ({ ...current, ingredientes: current.ingredientes.map((item, index) => index === originalIndex ? { ...item, estado } : item) }));
    if (preview) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/listascompra/id/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idIngrediente: ingredientId, estadoNuevo: estado }) });
      if (!response.ok) throw new Error('No se pudo actualizar el producto');
    } catch (error) {
      console.error(error);
      setList((current) => ({ ...current, ingredientes: current.ingredientes.map((item, index) => index === originalIndex ? { ...item, estado: previous } : item) }));
    }
  };

  const enriched = useMemo(() => (list?.ingredientes || []).map((item, originalIndex) => {
    const data = ingredients.find((ingredient) => ingredient._id === item.idIngrediente);
    return { ...item, originalIndex, name: data?.name || 'Ingrediente', unit: item.unit || data?.standardUnit || '', category: data?.subcategoria || 'Otros' };
  }), [ingredients, list]);
  const visible = enriched.filter((item) => (showBought || item.estado !== 'comprado') && item.name.toLowerCase().includes(search.toLowerCase()));
  const grouped = visible.reduce((acc, item) => ({ ...acc, [item.category]: [...(acc[item.category] || []), item] }), {});
  const bought = enriched.filter((item) => item.estado === 'comprado').length;
  const percent = enriched.length ? Math.round((bought / enriched.length) * 100) : 0;

  if (loading) return <div className="lists-v2-page"><div className="lists-v2-empty">Preparando tu lista…</div></div>;
  if (!list) return <div className="lists-v2-page"><div className="lists-v2-empty">No encontramos esta lista.</div></div>;

  const shareUrl = `https://wa.me/?text=${encodeURIComponent(`Lista de la compra: ${list.nombre} ${window.location.href}`)}`;
  return (
    <ExperimentalPopupProvider>
      <main className="lists-v2-page list-detail-v2">
        <header className="list-detail-v2-header">
          <Link className="lists-v2-back" to={preview ? '/preview/listas' : '/listas'}><ArrowBack /> <span>Mis listas</span></Link>
          <div className="list-detail-v2-title"><span>Lista activa</span><h1>{list.nombre}</h1><p>{enriched.length} productos · creada el {new Date(list.createdAt).toLocaleDateString('es-ES')}</p></div>
          <div className="list-detail-v2-actions"><a href={shareUrl} target="_blank" rel="noreferrer"><WhatsApp /> <span>Compartir</span></a><button aria-label="Más opciones"><MoreHoriz /></button></div>
        </header>

        <section className="list-detail-v2-status">
          <div className="list-detail-v2-status-copy"><div className="lists-v2-ring" style={{ '--progress': `${percent * 3.6}deg` }}><strong>{percent}%</strong></div><div><strong>{percent === 100 ? '¡Compra completada!' : `${enriched.length - bought} productos pendientes`}</strong><span>{bought} de {enriched.length} en la cesta</span></div></div>
          <div className="list-detail-v2-status-bar"><span style={{ width: `${percent}%` }} /></div>
          <div className="list-detail-v2-filters"><label className="lists-v2-search"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar producto" /></label>{bought > 0 && <button onClick={() => setShowBought((value) => !value)}>{showBought ? <ExpandLess /> : <ExpandMore />}{showBought ? 'Ocultar comprados' : `Mostrar comprados (${bought})`}</button>}</div>
        </section>

        <section className="list-detail-v2-categories">
          {Object.keys(grouped).sort().map((category) => <article key={category} className="list-detail-v2-category">
            <div className="list-detail-v2-category-head"><h2>{category}</h2><span>{grouped[category].filter((item) => item.estado !== 'comprado').length} pendientes</span></div>
            <div className="list-detail-v2-items">{grouped[category].map((item) => {
              const checked = item.estado === 'comprado';
              return <button className={checked ? 'is-checked' : ''} key={`${item.idIngrediente}-${item.originalIndex}`} onClick={() => handleToggle(item.originalIndex, item.idIngrediente)}>
                <span className="list-detail-v2-check">{checked && <Check />}</span><span className="list-detail-v2-item-name"><strong>{item.name}</strong><small>{item.cantidad} {item.unit}</small></span>{checked && <CheckCircle className="list-detail-v2-done" />}
              </button>;
            })}</div>
          </article>)}
          {!Object.keys(grouped).length && <div className="lists-v2-empty"><ShoppingCartCheckout /><h2>{!search && bought === enriched.length ? '¡Todo comprado!' : 'Sin resultados'}</h2><p>{!search && bought === enriched.length ? 'Puedes mostrar los productos comprados para revisar la lista.' : 'Cambia el filtro para ver más productos.'}</p></div>}
        </section>
      </main>
    </ExperimentalPopupProvider>
  );
}
